import { useMutation } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import LabelWithIndicator from 'components/atoms/LabelWithIndicator';
import {
  DisplayKey,
  EmptyKey,
  GenerateKey
} from 'components/molecules/IntegrateApi';
import {
  DisplayGuideHelper,
  DisplayIntegrationInfo
} from 'components/molecules/IntegrationsCrud';
import RayoToast from 'components/RayoToast';
import {
  DELETE_API_KEY,
  GENERATE_API_KEY
} from 'graphql/mutations/integrations';
import PropTypes from 'prop-types';
import React from 'react';
import toast from 'react-hot-toast';
import { useRecoilValue } from 'recoil';
import { currentUser } from 'recoil/User';

const IntegrateApi = ({ selectedIntegration, refetch }) => {
  const theme = useTheme();
  const user = useRecoilValue(currentUser);
  const [genApiKey, { loading }] = useMutation(GENERATE_API_KEY);
  const [deleteApiKey] = useMutation(DELETE_API_KEY);

  const [isEnableCreate, setIsEnableCreate] = React.useState(false);

  const [apiKey, setApiKey] = React.useState(
    selectedIntegration?.integrationsData[0] || null
  );
  const [inputName, setInputName] = React.useState(
    selectedIntegration?.integrationsData[0]?.name
  );

  const handleGenerateApiKey = async () => {
    try {
      toast.loading('Generando nueva API KEY', { id: 'genApiKey' });
      const variables = {
        ...(['ODIN'].includes(user?.role) && {
          clientId: selectedIntegration?.clientId
        }),
        name: inputName
      };
      if (apiKey) {
        await deleteApiKey({
          variables: { clientId: selectedIntegration.clientId }
        });
      }
      await genApiKey({
        variables,
        onCompleted: (tData) => {
          setApiKey({ name: inputName, key: tData?.generateApiKey?.apiKey });
          setIsEnableCreate(false);
        }
      });
      refetch();
      toast.success('Se ha generado una nueva API KEY!', {
        id: 'genApiKey',
        duration: 5000
      });
    } catch (error) {
      toast(
        <RayoToast
          message="Algo salió mal, intentalo de nuevo!"
          err={error}
          id="genApiKey"
        />,
        {
          id: 'genApiKey',
          duration: Infinity
        }
      );
    }
  };

  const handleRegenerateKey = () => setIsEnableCreate(true);

  return (
    <Grid container justifyContent="center" columnSpacing={2}>
      <Grid lg={8}>
        <Stack
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            height: '100%'
          }}
        >
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid lg={8}>
              <DisplayIntegrationInfo
                title={selectedIntegration?.title}
                imageUrl={selectedIntegration?.imageUrl}
                subtitle={
                  apiKey ? (
                    <LabelWithIndicator>Integrado</LabelWithIndicator>
                  ) : (
                    <Typography variant="body2" color={theme.palette.grey[600]}>
                      No Integrado
                    </Typography>
                  )
                }
                description={selectedIntegration?.description}
              />
            </Grid>
            <Grid lg={4}>
              {selectedIntegration?.guideUrl && (
                <DisplayGuideHelper
                  integrationTitle={selectedIntegration.title}
                  urlHelper={selectedIntegration?.guideUrl}
                />
              )}
            </Grid>
            <Grid lg={12}>
              <Stack
                sx={{
                  maxHeight: '60vh',
                  minHeight: '50vh',
                  overflowY: 'auto',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 4
                }}
                spacing={2}
              >
                {!apiKey || isEnableCreate ? (
                  <Stack
                    sx={{
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2
                    }}
                    alignItems="center"
                    spacing={2}
                  >
                    {isEnableCreate ? (
                      <GenerateKey
                        {...{
                          inputName,
                          setInputName,
                          handleGenerateApiKey,
                          loading,
                          setIsEnableCreate
                        }}
                      />
                    ) : (
                      <EmptyKey {...{ setIsEnableCreate }} />
                    )}
                  </Stack>
                ) : (
                  <DisplayKey
                    {...{
                      apiKey: { name: apiKey?.name, key: apiKey?.key },
                      handleRegenerateKey
                    }}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

IntegrateApi.propTypes = {
  selectedIntegration: PropTypes.shape({
    clientId: PropTypes.string,
    counter: PropTypes.number,
    description: PropTypes.string,
    guideUrl: PropTypes.string,
    id: PropTypes.string,
    imageUrl: PropTypes.string,
    indexReference: PropTypes.number,
    integrationsData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isEmphasized: PropTypes.bool,
    message: PropTypes.string,
    metadata: PropTypes.object,
    title: PropTypes.string
  }),
  refetch: PropTypes.func
};

export default IntegrateApi;
